<template>
  <ListComponent title="Codeco Raw Events" :totalCount="totalItems">
    <template #actions>
      <!--
      <v-btn outlined small color="primary" @click="showSummaryDialog = true">
        <v-icon left small>mdi-chart-box-outline</v-icon>
        Summary
      </v-btn>
      -->
      <v-btn outlined small color="primary" @click="openBreakdownDialog">
        <v-icon left small>mdi-file-tree-outline</v-icon>
        Breakdown
      </v-btn>
      <v-btn
        v-if="specialEditPermissions && specialEditPermissions.change"
        outlined
        small
        color="warning"
        class="ml-2"
        @click="openRequeueDialog"
      >
        <v-icon left small>mdi-restart</v-icon>
        Requeue Failed
      </v-btn>
    </template>
    <template #list>
      <v-row class="ma-0" dense>
        <v-col cols="6" md="3" class="pl-0 pt-0 pb-2">
          <v-text-field
            label="Search (Container No / VBS Ref / Voyage ID)"
            outlined
            dense
            append-icon="mdi-magnify"
            clearable
            hide-details="auto"
            v-model="search"
            @click:clear="onSearchClear"
            @keyup.enter="onSearch"
          />
        </v-col>
        <v-col cols="6" md="auto" style="min-width:150px" class="pt-0 pb-2">
          <MultiSelect
            label="Category"
            :items="categoryList"
            itemText="text"
            itemValue="value"
            clearable
            dense
            hide-details="auto"
            v-model="filters.category"
            @change="onFilterChange"
          />
        </v-col>
        <v-col cols="6" md="auto" style="min-width:140px" class="pt-0 pb-2">
          <MultiSelect
            label="DA Created"
            :items="daCreatedList"
            itemText="text"
            itemValue="value"
            clearable
            dense
            hide-details="auto"
            v-model="filters.da_created"
            @change="onFilterChange"
          />
        </v-col>
        <v-col cols="6" md="auto" style="min-width:150px" class="pt-0 pb-2">
          <MultiSelect
            label="Sub Hire / Own"
            :items="subHireList"
            itemText="text"
            itemValue="value"
            clearable
            dense
            hide-details="auto"
            v-model="filters.is_subhire"
            @change="onFilterChange"
          />
        </v-col>
        <v-col cols="6" md="auto" style="min-width:160px" class="pt-0 pb-2">
          <DateField
            v-model="filters.event_timestamp_from"
            :inputProps="{
              dense: true,
              outlined: true,
              label: 'Event Date From',
              'hide-details': 'auto',
              clearable: true,
            }"
            @change="onFilterChange"
          />
        </v-col>
        <v-col cols="6" md="auto" style="min-width:160px" class="pt-0 pb-2">
          <DateField
            v-model="filters.event_timestamp_to"
            :inputProps="{
              dense: true,
              outlined: true,
              label: 'Event Date To',
              'hide-details': 'auto',
              clearable: true,
            }"
            @change="onFilterChange"
          />
        </v-col>
      </v-row>
      <v-row class="ma-0" dense>
        <v-col cols="12" md="7" class="pl-0 pt-0 pb-2 d-flex align-center">
          <v-tabs height="35" v-model="tab">
            <v-tabs-slider></v-tabs-slider>
            <v-tab>
              Pending
              <v-badge
                v-if="statusCounts.pending"
                top
                :content="statusCounts.pending"
                overlap
                class="mb-4 ml-3"
              >
              </v-badge>
            </v-tab>
            <v-tab>
              Processing
              <v-badge
                v-if="statusCounts.processing"
                top
                :content="statusCounts.processing"
                overlap
                class="mb-4 ml-3"
              >
              </v-badge>
            </v-tab>
            <v-tab>
              Completed
              <v-badge
                v-if="statusCounts.completed"
                top
                :content="statusCounts.completed"
                overlap
                class="mb-4 ml-3"
              >
              </v-badge>
            </v-tab>
            <v-tab>
              Failed
              <v-badge
                v-if="statusCounts.failed"
                top
                :content="statusCounts.failed"
                overlap
                class="mb-4 ml-3"
              >
              </v-badge>
            </v-tab>
          </v-tabs>
        </v-col>
        <v-col cols="12" md="5" class="pr-0 pt-0 pb-2 d-flex align-center justify-end" style="gap:8px">
          <v-btn outlined small color="primary" to="/app/admin/report_center">
            <v-icon left small>mdi-download</v-icon>
            Export
          </v-btn>
          <Pagination
            v-if="totalItems > 0"
            :pageNo="pageNo"
            :totalItems="totalItems"
            :pageSize="itemsPerPage"
            @itemsPerPageChange="itemsPerPageChanged"
            @prevPage="prevPage"
            @nextPage="nextPage"
          />
        </v-col>
      </v-row>
      <v-row class="ma-0" dense>
        <v-col cols="12" class="pa-0">
          <AgGridVue
            style="width: 100%; height: calc(100vh - 260px)"
            class="ag-theme-alpine"
            :columnDefs="headers"
            :context="context"
            @grid-ready="onGridReady"
            :defaultColDef="defaultColDef"
            :grid-options="gridOptions"
            :rowData="allList"
          />
        </v-col>
      </v-row>
    </template>

    <template #dialogs>
      <v-dialog v-model="showSummaryDialog" max-width="900">
        <v-card>
          <v-card-title class="d-flex align-center">
            <span>Summary</span>
            <v-tooltip bottom max-width="340">
              <template #activator="{ on, attrs }">
                <v-icon small color="grey darken-1" class="ml-1" v-bind="attrs" v-on="on">mdi-information-outline</v-icon>
              </template>
              <span>
                <strong>Total / Bonded / Non Bonded</strong> count raw CODECO events (a container can have
                both a Gate Out and a Gate In row, so these can exceed the container count).
                <strong>DA Created, Draft DA Only, ML/Sub-Hire (Bonded/Non-Bonded)</strong> count
                <strong>distinct containers</strong> instead - one per container, matching the Draft DA list's
                own counts - so they will not sum up to Total.
              </span>
            </v-tooltip>
            <v-spacer />
            <v-btn icon small @click="showSummaryDialog = false">
              <v-icon small>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text>
            <div class="dashboard-tiles">
              <v-card outlined class="pa-2 text-center dashboard-tile" style="border-color:#1976d2">
                <div class="text-caption font-weight-bold text-uppercase grey--text text--darken-2">
                  Total
                </div>
                <div class="text-h6 font-weight-bold primary--text">{{ dashboard.total ?? '-' }}</div>
              </v-card>
              <v-card
                outlined
                class="pa-2 text-center dashboard-tile dashboard-tile--clickable"
                :class="{ 'dashboard-tile--active': filters.category === 'bonded' }"
                @click="toggleCategoryFilter('bonded')"
              >
                <div class="text-caption font-weight-bold text-uppercase grey--text text--darken-2">
                  Bonded
                </div>
                <div class="text-h6 font-weight-bold">{{ dashboard.bonded ?? '-' }}</div>
              </v-card>
              <v-card
                outlined
                class="pa-2 text-center dashboard-tile dashboard-tile--clickable"
                :class="{ 'dashboard-tile--active': filters.category === 'non_bonded' }"
                @click="toggleCategoryFilter('non_bonded')"
              >
                <div class="text-caption font-weight-bold text-uppercase grey--text text--darken-2">
                  Non Bonded
                </div>
                <div class="text-h6 font-weight-bold">{{ dashboard.non_bonded ?? '-' }}</div>
              </v-card>
              <v-card outlined class="pa-2 text-center dashboard-tile" style="border-color:#4caf50">
                <div class="text-caption font-weight-bold text-uppercase grey--text text--darken-2">
                  DA Created
                </div>
                <div class="text-h6 font-weight-bold success--text">{{ dashboard.da_created ?? '-' }}</div>
              </v-card>
              <v-card outlined class="pa-2 text-center dashboard-tile" style="border-color:#ff7043">
                <div class="text-caption font-weight-bold text-uppercase grey--text text--darken-2">
                  Draft DA Only
                </div>
                <div class="text-h6 font-weight-bold" style="color:#ff7043">{{ dashboard.draft_da_only ?? '-' }}</div>
              </v-card>
            </div>
            <div class="dashboard-tiles mt-2">
              <v-card
                outlined
                class="pa-2 text-center dashboard-tile dashboard-tile--clickable"
                :class="{ 'dashboard-tile--active': filters.category === 'ml_bonded' }"
                @click="toggleCategoryFilter('ml_bonded')"
              >
                <div class="text-caption font-weight-bold text-uppercase grey--text text--darken-2">
                  ML Bonded
                </div>
                <div class="text-h6 font-weight-bold">{{ dashboard.ml_bonded ?? '-' }}</div>
              </v-card>
              <v-card
                outlined
                class="pa-2 text-center dashboard-tile dashboard-tile--clickable"
                :class="{ 'dashboard-tile--active': filters.category === 'subhire_bonded' }"
                @click="toggleCategoryFilter('subhire_bonded')"
              >
                <div class="text-caption font-weight-bold text-uppercase grey--text text--darken-2">
                  Sub-Hire Bonded
                </div>
                <div class="text-h6 font-weight-bold">{{ dashboard.subhire_bonded ?? '-' }}</div>
              </v-card>
              <v-card
                outlined
                class="pa-2 text-center dashboard-tile dashboard-tile--clickable"
                :class="{ 'dashboard-tile--active': filters.category === 'subhire_non_bonded' }"
                @click="toggleCategoryFilter('subhire_non_bonded')"
              >
                <div class="text-caption font-weight-bold text-uppercase grey--text text--darken-2">
                  Sub-Hire Non-Bonded
                </div>
                <div class="text-h6 font-weight-bold">{{ dashboard.subhire_non_bonded ?? '-' }}</div>
              </v-card>
              <v-card
                outlined
                class="pa-2 text-center dashboard-tile dashboard-tile--clickable"
                :class="{ 'dashboard-tile--active': filters.category === 'ml_non_bonded' }"
                @click="toggleCategoryFilter('ml_non_bonded')"
              >
                <div class="text-caption font-weight-bold text-uppercase grey--text text--darken-2">
                  ML Non-Bonded
                </div>
                <div class="text-h6 font-weight-bold">{{ dashboard.ml_non_bonded ?? '-' }}</div>
              </v-card>
            </div>
          </v-card-text>
        </v-card>
      </v-dialog>

      <v-dialog v-model="showBreakdownDialog" max-width="700">
        <v-card>
          <v-card-title class="d-flex align-center">
            <span>Breakdown</span>
            <v-spacer />
            <v-btn icon small @click="showBreakdownDialog = false">
              <v-icon small>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text>
            <v-row class="ma-0 mb-2" dense>
              <v-col cols="6" class="pl-0 pt-0 pb-2">
                <DateField
                  v-model="breakdownFilters.event_timestamp_from"
                  :inputProps="{
                    dense: true,
                    outlined: true,
                    label: 'Event Date From',
                    'hide-details': 'auto',
                    clearable: true,
                  }"
                  @change="fetchBreakdown"
                />
              </v-col>
              <v-col cols="6" class="pr-0 pt-0 pb-2">
                <DateField
                  v-model="breakdownFilters.event_timestamp_to"
                  :inputProps="{
                    dense: true,
                    outlined: true,
                    label: 'Event Date To',
                    'hide-details': 'auto',
                    clearable: true,
                  }"
                  @change="fetchBreakdown"
                />
              </v-col>
            </v-row>
            <v-skeleton-loader v-if="breakdownLoading" type="list-item@6" />
            <div v-else-if="!breakdown" class="body-text grey--text text-center py-8">
              No data to show.
            </div>
            <div v-else class="breakdown-tree">
              <div class="breakdown-row breakdown-row--total">
                <span class="breakdown-label">Total</span>
                <span class="breakdown-value">{{ breakdown.total }}</span>
              </div>

              <div class="breakdown-row breakdown-row--l1">
                <span class="breakdown-label">Bonded</span>
                <span class="breakdown-value">{{ breakdown.bonded.total }}</span>
              </div>
              <div class="breakdown-row breakdown-row--l2">
                <span class="breakdown-label">ML Bonded</span>
                <span class="breakdown-value">{{ breakdown.bonded.ml.total }}</span>
              </div>
              <div class="breakdown-row breakdown-row--l3">
                <span class="breakdown-label">DA Created</span>
                <span class="breakdown-value success--text">{{ breakdown.bonded.ml.da_created }}</span>
              </div>
              <div class="breakdown-row breakdown-row--l3">
                <span class="breakdown-label">Draft DA Only</span>
                <span class="breakdown-value" style="color:#ff7043">{{ breakdown.bonded.ml.draft_da_only }}</span>
              </div>
              <div class="breakdown-row breakdown-row--l2">
                <span class="breakdown-label">Subhire Bonded</span>
                <span class="breakdown-value">{{ breakdown.bonded.subhire.total }}</span>
              </div>
              <div class="breakdown-row breakdown-row--l3">
                <span class="breakdown-label">DA Created</span>
                <span class="breakdown-value success--text">{{ breakdown.bonded.subhire.da_created }}</span>
              </div>
              <div class="breakdown-row breakdown-row--l3">
                <span class="breakdown-label">Draft DA Only</span>
                <span class="breakdown-value" style="color:#ff7043">{{ breakdown.bonded.subhire.draft_da_only }}</span>
              </div>

              <div class="breakdown-row breakdown-row--l1 mt-3">
                <span class="breakdown-label">Non Bonded</span>
                <span class="breakdown-value">{{ breakdown.non_bonded.total }}</span>
              </div>
              <div class="breakdown-row breakdown-row--l2">
                <span class="breakdown-label">ML Non-Bonded</span>
                <span class="breakdown-value">{{ breakdown.non_bonded.ml }}</span>
              </div>
              <div class="breakdown-row breakdown-row--l2">
                <span class="breakdown-label">Subhire Non-Bonded</span>
                <span class="breakdown-value">{{ breakdown.non_bonded.subhire }}</span>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-dialog>

      <v-dialog v-model="showRequeueDialog" max-width="1100" persistent scrollable>
        <v-card>
          <v-card-title class="d-flex align-center">
            <span>Requeue Failed Events</span>
            <v-spacer />
            <v-btn icon small @click="closeRequeueDialog">
              <v-icon small>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text>
            <div class="d-flex align-center px-0 py-2">
              <span class="font-weight-bold text-body-1">
                {{ requeueTotalCount }} failed row{{ requeueTotalCount === 1 ? "" : "s" }}
                <span class="font-weight-regular grey--text">— {{ requeueSelectedIds.length }} selected</span>
              </span>
              <v-spacer />
              <Pagination
                v-if="requeueTotalCount > 0"
                :pageNo="requeuePageNo"
                :totalItems="requeueTotalCount"
                :pageSize="requeueItemsPerPage"
                @itemsPerPageChange="onRequeueItemsPerPageChange"
                @prevPage="prevRequeuePage"
                @nextPage="nextRequeuePage"
              />
            </div>
            <v-data-table
              :value="requeueSelectedRowsOnPage"
              @input="onRequeueSelectionChanged"
              :headers="requeueHeaders"
              :items="requeueRows"
              item-key="id"
              show-select
              hide-default-footer
              disable-pagination
              disable-sort
              :loading="requeueLoading"
              :items-per-page="requeueItemsPerPage"
            >
              <template v-slot:[`item.failure_reason`]="{ item }">
                <span class="text-body-2 error--text" style="display:block; max-width:420px; white-space:normal">
                  {{ item.failure_reason }}
                </span>
              </template>
              <template v-slot:no-data>
                <div class="py-6 text-center grey--text">No failed rows.</div>
              </template>
            </v-data-table>
          </v-card-text>
          <v-divider />
          <v-card-actions class="px-4 py-3">
            <span class="caption grey--text">Resets selected rows to Pending and clears their failure reason.</span>
            <v-spacer />
            <v-btn depressed outlined @click="closeRequeueDialog" class="mr-2">Cancel</v-btn>
            <v-btn
              color="warning"
              depressed
              :loading="requeueSubmitting"
              :disabled="!requeueSelectedIds.length"
              @click="submitRequeue"
            >
              <v-icon small class="mr-1">mdi-restart</v-icon>
              Requeue ({{ requeueSelectedIds.length }})
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>
  </ListComponent>
</template>
<script>
import ListComponent from "@/components/General/ListComponent.vue";
import { AgGridVue } from "ag-grid-vue";
import CustomHeader from "@/components/AgGridComponents/General/CustomHeader.vue";
import Pagination from "@/components/General/Pagination.vue";
import CodecoRawEventStatusChip from "@/components/AgGridComponents/CodecoRawEventStatusChip.vue";
import MultiSelect from "@/components/FormBaseComponents/MultiSelect.vue";
import DateField from "@/components/FormBaseComponents/DateField.vue";
import {
  isAuthenticated,
  hasRouteAccess,
  getPermissions,
} from "@/utils/functions";

export default {
  components: {
    ListComponent,
    agColumnHeader: CustomHeader,
    AgGridVue,
    Pagination,
    CodecoRawEventStatusChip,
    MultiSelect,
    DateField,
  },
  data() {
    return {
      allList: [],
      totalItems: 0,
      search: "",
      tab: 0,
      statusCounts: {},
      statusByTab: ["pending", "processing", "completed", "failed"],
      filters: {
        event_timestamp_from: null,
        event_timestamp_to: null,
        category: null,
        da_created: null,
        is_subhire: null,
      },
      categoryList: [
        { text: "Bonded", value: "bonded" },
        { text: "Non Bonded", value: "non_bonded" },
      ],
      daCreatedList: [
        { text: "Yes", value: true },
        { text: "No", value: false },
      ],
      subHireList: [
        { text: "Sub-Hire", value: "Yes" },
        { text: "Own Fleet", value: "No" },
      ],
      dashboard: {},
      showSummaryDialog: false,
      showBreakdownDialog: false,
      breakdownFilters: {
        event_timestamp_from: null,
        event_timestamp_to: null,
      },
      breakdownLoading: false,
      breakdown: null,
      specialEditPermissions: null,
      showRequeueDialog: false,
      requeueLoading: false,
      requeueSubmitting: false,
      requeueRows: [],
      requeueTotalCount: 0,
      requeuePageNo: 1,
      requeueItemsPerPage: 50,
      requeueSelectedIds: [],
      requeueHeaders: [
        { text: "Container No", value: "container_number", width: 150 },
        { text: "VBS Ref No", value: "vbs_reference_no", width: 150 },
        { text: "Voyage ID", value: "voyage_id", width: 160 },
        { text: "Event", value: "event_type", width: 100 },
        { text: "Failure Reason", value: "failure_reason" },
      ],
      pageNo: 1,
      itemsPerPage: 50,
      permissions: null,
      defaultColDef: { resizable: true },
      gridOptions: {
        onGridSizeChanged: () => this.gridOptions.api.sizeColumnsToFit(),
        enableCellTextSelection: true,
        rowHeight: 35,
      },
      headers: [
        {
          headerName: "Status",
          field: "status",
          sortable: true,
          minWidth: 120,
          cellRendererFramework: "CodecoRawEventStatusChip",
        },
        {
          headerName: "Event",
          field: "event_type",
          sortable: true,
          minWidth: 110,
          valueFormatter: (params) =>
            params.value === "gate_in" ? "Gate In" : "Gate Out",
        },
        {
          headerName: "Container No",
          field: "container_number",
          sortable: true,
          minWidth: 160,
          cellClass: (params) =>
            params.data && params.data.emergency_order_container
              ? "primary--text font-weight-bold cursor-pointer"
              : "",
          onCellClicked: (params) => this.openContainerDetails(params.data),
        },
        {
          headerName: "Draft DA",
          field: "draft_da_no",
          sortable: false,
          minWidth: 150,
          valueFormatter: (params) => params.value || "-",
          cellClass: (params) =>
            params.data && params.data.draft_da_id
              ? "primary--text font-weight-bold cursor-pointer"
              : "",
          onCellClicked: (params) => this.openDraftDADetails(params.data),
        },
        {
          headerName: "DA No",
          field: "da_no",
          sortable: false,
          minWidth: 150,
          valueFormatter: (params) => params.value || "-",
          cellClass: (params) =>
            params.data && params.data.da_id
              ? "primary--text font-weight-bold cursor-pointer"
              : "",
          onCellClicked: (params) => this.openDaDetails(params.data),
        },
        {
          headerName: "Terminal",
          field: "terminal",
          sortable: true,
          minWidth: 110,
          valueFormatter: (params) => params.value || "-",
        },
        {
          headerName: "Container Type",
          field: "container_type",
          sortable: true,
          minWidth: 140,
          valueFormatter: (params) => params.value || "-",
        },
        {
          headerName: "Origin",
          field: "origin",
          sortable: true,
          minWidth: 110,
          valueFormatter: (params) => params.value || "-",
        },
        {
          headerName: "Destination",
          field: "destination",
          sortable: true,
          minWidth: 110,
          valueFormatter: (params) => params.value || "-",
        },
        {
          headerName: "Driver ID",
          field: "driver_id",
          sortable: true,
          minWidth: 120,
          valueFormatter: (params) => params.value || "-",
        },
        {
          headerName: "Vehicle No",
          field: "vehicle_no",
          sortable: true,
          minWidth: 130,
          valueFormatter: (params) => params.value || "-",
        },
        {
          headerName: "Vendor",
          field: "vendor_name",
          sortable: true,
          minWidth: 150,
          valueFormatter: (params) => params.value || "-",
        },
        {
          headerName: "VBS Ref No",
          field: "vbs_reference_no",
          sortable: true,
          minWidth: 150,
          valueFormatter: (params) => params.value || "-",
        },
        {
          headerName: "Voyage ID",
          field: "voyage_id",
          sortable: true,
          minWidth: 150,
          valueFormatter: (params) =>
            params.value || params.data?.emergency_order_container_voyage_id || "-",
        },
        {
          headerName: "Event Timestamp",
          field: "event_timestamp",
          sortable: true,
          minWidth: 170,
          valueFormatter: (params) =>
            params.value ? this.$globals.formatDate(params.value, true) : "-",
        },
        {
          headerName: "Processed On",
          field: "processed_on",
          sortable: true,
          minWidth: 170,
          valueFormatter: (params) =>
            params.value ? this.$globals.formatDate(params.value, true) : "-",
        },
        {
          headerName: "Created",
          field: "created",
          sortable: true,
          minWidth: 160,
          valueFormatter: (params) =>
            params.value ? this.$globals.formatDate(params.value, true) : "-",
        },
        {
          headerName: "Failure Reason",
          field: "failure_reason",
          sortable: false,
          minWidth: 320,
          tooltipValueGetter: (params) => params.value || "",
          valueFormatter: (params) => params.value || "-",
          cellClass: (params) => (params.value ? "red--text" : ""),
        },
      ],
    };
  },
  computed: {
    context() {
      return { parentComponent: this };
    },
    offset() {
      return this.itemsPerPage * (this.pageNo - 1);
    },
    requeueOffset() {
      return this.requeueItemsPerPage * (this.requeuePageNo - 1);
    },
    requeueSelectedRowsOnPage() {
      return this.requeueRows.filter((row) => this.requeueSelectedIds.includes(row.id));
    },
  },
  watch: {
    tab() {
      this.pageNo = 1;
      this.getList();
      this.getStatusCounts();
      this.getDashboard();
    },
  },
  methods: {
    commonFilterParams() {
      const params = {};
      if (this.search) {
        params.search = this.search;
      }
      if (this.filters.event_timestamp_from) {
        params.event_timestamp_from = this.filters.event_timestamp_from;
      }
      if (this.filters.event_timestamp_to) {
        params.event_timestamp_to = this.filters.event_timestamp_to;
      }
      if (this.filters.category) {
        params.category = this.filters.category;
      }
      if (this.filters.da_created !== null && this.filters.da_created !== undefined) {
        params.da_created = this.filters.da_created;
      }
      if (this.filters.is_subhire) {
        params.is_subhire = this.filters.is_subhire;
      }
      return params;
    },
    getDashboard() {
      const params = {
        ...this.commonFilterParams(),
        status: this.statusByTab[this.tab],
      };
      this.$api.codecoRawEvent
        .getCodecoRawEventDashboardSummary(params)
        .then((res) => {
          this.dashboard = res.data || res;
        })
        .catch((err) => {
          console.error(err);
        });
    },
    openContainerDetails(row) {
      if (row && row.emergency_order_container) {
        this.$router.push({
          path: `/app/admin/emergency-order-container/details/${row.emergency_order_container}`,
          query: {},
        });
      }
    },
    openDraftDADetails(row) {
      if (row && row.draft_da_id) {
        this.$router.push({
          path: `/app/admin/draft-da/details/${row.draft_da_id}`,
          query: {},
        });
      }
    },
    openDaDetails(row) {
      if (row && row.da_id) {
        this.$router.push({
          path: `/app/admin/delivery-advice/details/${row.da_id}`,
          query: {},
        });
      }
    },
    onSearch() {
      this.pageNo = 1;
      this.getList();
    },
    onSearchClear() {
      this.search = "";
      this.pageNo = 1;
      this.getList();
    },
    onFilterChange() {
      this.pageNo = 1;
      this.getList();
      this.getStatusCounts();
      this.getDashboard();
    },
    toggleCategoryFilter(value) {
      this.filters.category = this.filters.category === value ? null : value;
      this.onFilterChange();
    },
    itemsPerPageChanged(e) {
      this.pageNo = 1;
      this.itemsPerPage = e;
      this.getList();
    },
    prevPage() {
      this.pageNo--;
      this.getList();
    },
    nextPage() {
      this.pageNo++;
      this.getList();
    },
    onGridReady(params) {
      this.gridApi = params.api;
      this.gridColumnApi = params.columnApi;
    },
    getList() {
      this.$bus.$emit("showLoader", true);
      const params = {
        ...this.commonFilterParams(),
        offset: this.offset,
        limit: this.itemsPerPage,
        status: this.statusByTab[this.tab],
      };
      this.$api.codecoRawEvent
        .getCodecoRawEventList(params)
        .then((res) => {
          this.allList = res.data;
          this.totalItems = res.count != null ? res.count : res.data.length;
          this.$bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showLoader", false);
        });
    },
    getStatusCounts() {
      const params = this.commonFilterParams();
      this.$api.codecoRawEvent
        .getCodecoRawEventStatusCounts(params)
        .then((res) => {
          this.statusCounts = res.data || res;
        })
        .catch((err) => {
          console.error(err);
        });
    },
    openBreakdownDialog() {
      this.showBreakdownDialog = true;
      this.breakdownFilters.event_timestamp_from = this.filters.event_timestamp_from;
      this.breakdownFilters.event_timestamp_to = this.filters.event_timestamp_to;
      this.fetchBreakdown();
    },
    fetchBreakdown() {
      this.breakdownLoading = true;
      const params = {
        ...this.commonFilterParams(),
        status: this.statusByTab[this.tab],
      };
      if (this.breakdownFilters.event_timestamp_from) {
        params.event_timestamp_from = this.breakdownFilters.event_timestamp_from;
      }
      if (this.breakdownFilters.event_timestamp_to) {
        params.event_timestamp_to = this.breakdownFilters.event_timestamp_to;
      }
      this.$api.codecoRawEvent
        .getCodecoRawEventBreakdown(params)
        .then((res) => {
          this.breakdown = res.data || res;
        })
        .catch((err) => {
          this.$bus.$emit("showToastMessage", {
            message: err?.message || "Failed to load breakdown.",
            color: "error",
          });
        })
        .finally(() => {
          this.breakdownLoading = false;
        });
    },
    openRequeueDialog() {
      this.showRequeueDialog = true;
      this.requeuePageNo = 1;
      this.requeueSelectedIds = [];
      this.fetchRequeueRows();
    },
    closeRequeueDialog() {
      this.showRequeueDialog = false;
    },
    fetchRequeueRows() {
      this.requeueLoading = true;
      this.$api.codecoRawEvent
        .getCodecoRawEventFailedRows({ offset: this.requeueOffset, limit: this.requeueItemsPerPage })
        .then((res) => {
          const data = res.data || res;
          this.requeueRows = data.rows || [];
          this.requeueTotalCount = data.count != null ? data.count : this.requeueRows.length;
        })
        .catch((err) => {
          this.$bus.$emit("showToastMessage", {
            message: err?.message || "Failed to load failed rows.",
            color: "error",
          });
        })
        .finally(() => {
          this.requeueLoading = false;
        });
    },
    onRequeueSelectionChanged(selectedOnPage) {
      const selectedIdsOnPage = selectedOnPage.map((r) => r.id);
      const pageIds = this.requeueRows.map((r) => r.id);
      const keptFromOtherPages = this.requeueSelectedIds.filter((id) => !pageIds.includes(id));
      this.requeueSelectedIds = [...keptFromOtherPages, ...selectedIdsOnPage];
    },
    onRequeueItemsPerPageChange(size) {
      this.requeuePageNo = 1;
      this.requeueItemsPerPage = size;
      this.fetchRequeueRows();
    },
    prevRequeuePage() {
      this.requeuePageNo--;
      this.fetchRequeueRows();
    },
    nextRequeuePage() {
      this.requeuePageNo++;
      this.fetchRequeueRows();
    },
    submitRequeue() {
      this.requeueSubmitting = true;
      this.$api.codecoRawEvent
        .requeueCodecoRawEventFailed({ ids: this.requeueSelectedIds })
        .then((res) => {
          const data = res.data || res;
          this.$bus.$emit("showToastMessage", {
            message: `${data.requeued || 0} row(s) requeued to Pending.`,
            color: "success",
          });
          this.requeueSelectedIds = [];
          this.fetchRequeueRows();
          this.getStatusCounts();
          this.getList();
        })
        .catch((err) => {
          this.$bus.$emit("showToastMessage", {
            message: err?.message || "Failed to requeue rows.",
            color: "error",
          });
        })
        .finally(() => {
          this.requeueSubmitting = false;
        });
    },
  },
  beforeMount() {
    if (!isAuthenticated()) {
      this.$router.push("/");
    } else if (!hasRouteAccess(this.$route)) {
      this.$router.push(`/${localStorage.getItem("user_type").toLowerCase()}`);
    } else {
      const permissions = getPermissions("emergency_order_container");
      if (!permissions || !permissions.view) {
        this.$bus.$emit("showToastMessage", {
          message: "You don't have access to this module!",
          color: "error",
        });
        this.$router.push(
          `/${localStorage.getItem("user_type").toLowerCase()}`,
        );
      } else {
        this.permissions = { ...permissions };
        this.specialEditPermissions = getPermissions("special_edit");
        this.getList();
        this.getStatusCounts();
        this.getDashboard();
      }
    }
  },
};
</script>
<style scoped>
.breakdown-tree {
  font-size: 14px;
}
.breakdown-row {
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
.breakdown-row--total {
  font-weight: 700;
  font-size: 16px;
}
.breakdown-row--l1 {
  font-weight: 700;
  padding-left: 16px;
}
.breakdown-row--l2 {
  padding-left: 32px;
  color: rgba(0, 0, 0, 0.75);
}
.breakdown-row--l3 {
  padding-left: 48px;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.6);
  border-bottom: none;
}
.breakdown-value {
  font-weight: 700;
}
.dashboard-tiles {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.dashboard-tile {
  flex: 1 1 calc(14.2857% - 8px);
  min-width: 130px;
}
.dashboard-tile--clickable {
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.dashboard-tile--clickable:hover {
  border-color: #1976d2;
}
.dashboard-tile--active {
  border-color: #1976d2 !important;
  border-width: 2px;
  box-shadow: 0 0 0 1px #1976d2;
}
</style>
